/**
 * Legacy heavy-POLYLINE family group-code readers (`POLYLINE`, `VERTEX`),
 * extracted from `entityParser.ts` to keep that monolith under the size
 * ceiling (CLAUDE.md "Контроль размера файлов").
 *
 * Follows the established seam convention (`entityParser.lineFamily.ts`,
 * `entityParser.hatchBoundary.ts`): the readers take a STRUCTURAL SLICE of
 * `SupportedEntityDraft` rather than importing the full draft type, so the
 * dependency edge is one-directional — `entityParser.ts` imports these
 * tables, nothing flows back — and no require cycle is introduced
 * (ПРИОРИТЕТ #2).
 *
 * Source of truth: Autodesk DXF reference (POLYLINE / VERTEX group codes) +
 * ezdxf 1.4.4 `entities/polyline.py` (`acdb_polyline`, `acdb_vertex`).
 * AutoCAD MCP is BANNED.
 */

import {
  type MutablePoint,
  parseNumberGroup,
  setPointComponent,
} from "./entityParser.groupValueHelpers";

/** The slice of `SupportedEntityDraft` this family reads and writes. */
type PolylineFamilyDraft = {
  points: Record<string, MutablePoint>;
  numbers: Record<string, number | undefined>;
};

type PolylineFamilyGroupReader = (
  draft: PolylineFamilyDraft,
  value: string,
) => void;

/**
 * Draft key prefix for the four poly-face VERTEX face-index codes. The code
 * is appended verbatim (`vertexFace71` … `vertexFace74`) so finalisation can
 * tell WHICH codes the file actually authored and re-emit at the same ones.
 */
export const VERTEX_FACE_INDEX_KEY_PREFIX = "vertexFace";

/** The four VERTEX group codes carrying poly-face vertex indices, in order. */
export const VERTEX_FACE_INDEX_CODES: readonly number[] = [71, 72, 73, 74];

/**
 * `POLYLINE` (AcDb2dPolyline / AcDb3dPolyline / AcDbPolygonMesh /
 * AcDbPolyFaceMesh).
 *
 * Thickness (39) and the OCS extrusion (210/220/230) intentionally have NO
 * entry here — they carry their usual meaning and flow through
 * `COMMON_GROUP_READERS`, exactly like LWPOLYLINE's.
 */
export const POLYLINE_GROUP_READERS: Record<
  number,
  PolylineFamilyGroupReader
> = {
  // 66 — obsolete "vertices follow" flag. Retained verbatim; never synthesised.
  66: (draft, value) => {
    draft.numbers.verticesFollow = parseNumberGroup(66, value);
  },
  // 10/20/30 — the "dummy point": X/Y are always 0, Z is the elevation.
  10: (draft, value) => {
    setPointComponent(draft.points, "elevation", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "elevation", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "elevation", "z", 30, value);
  },
  // 70 — polyline flags (closed / curve-fit / spline-fit / 3D / meshes /
  // closed-in-N / poly-face / continuous linetype). See DXF_POLYLINE_FLAG.
  70: (draft, value) => {
    draft.numbers.flags = parseNumberGroup(70, value);
  },
  // 40/41 — DEFAULT start / end width, inherited by every vertex that omits
  // its own 40/41.
  40: (draft, value) => {
    draft.numbers.defaultStartWidth = parseNumberGroup(40, value);
  },
  41: (draft, value) => {
    draft.numbers.defaultEndWidth = parseNumberGroup(41, value);
  },
  // 71/72 — polygon mesh M/N vertex counts, or poly-face vertex/face counts.
  71: (draft, value) => {
    draft.numbers.meshMCount = parseNumberGroup(71, value);
  },
  72: (draft, value) => {
    draft.numbers.meshNCount = parseNumberGroup(72, value);
  },
  // 73/74 — smooth-surface M/N densities.
  73: (draft, value) => {
    draft.numbers.smoothSurfaceMDensity = parseNumberGroup(73, value);
  },
  74: (draft, value) => {
    draft.numbers.smoothSurfaceNDensity = parseNumberGroup(74, value);
  },
  // 75 — curves and smooth-surface type (0 none / 5 quadratic / 6 cubic /
  // 8 Bezier).
  75: (draft, value) => {
    draft.numbers.surfaceType = parseNumberGroup(75, value);
  },
};

/**
 * `VERTEX` (AcDbVertex + AcDb2dVertex / AcDb3dPolylineVertex /
 * AcDbPolygonMeshVertex / AcDbPolyFaceMeshVertex / AcDbFaceRecord).
 *
 * Codes 71–74 are the poly-face FACE record's vertex indices; a negative
 * index marks the edge starting at that vertex invisible. They are stored
 * per-code (not pushed into one array) so finalisation can re-emit exactly
 * the codes the file authored — a face record that omits a code must not
 * gain one on export.
 */
export const VERTEX_GROUP_READERS: Record<number, PolylineFamilyGroupReader> = {
  10: (draft, value) => {
    setPointComponent(draft.points, "location", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "location", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "location", "z", 30, value);
  },
  40: (draft, value) => {
    draft.numbers.startWidth = parseNumberGroup(40, value);
  },
  41: (draft, value) => {
    draft.numbers.endWidth = parseNumberGroup(41, value);
  },
  42: (draft, value) => {
    draft.numbers.bulge = parseNumberGroup(42, value);
  },
  // 50 — curve-fit tangent direction, DEGREES (DXF group-code value types);
  // meaningful only when group 70 bit 2 is set. Stored verbatim.
  50: (draft, value) => {
    draft.numbers.curveFitTangent = parseNumberGroup(50, value);
  },
  70: (draft, value) => {
    draft.numbers.flags = parseNumberGroup(70, value);
  },
  71: (draft, value) => {
    draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}71`] = parseNumberGroup(
      71,
      value,
    );
  },
  72: (draft, value) => {
    draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}72`] = parseNumberGroup(
      72,
      value,
    );
  },
  73: (draft, value) => {
    draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}73`] = parseNumberGroup(
      73,
      value,
    );
  },
  74: (draft, value) => {
    draft.numbers[`${VERTEX_FACE_INDEX_KEY_PREFIX}74`] = parseNumberGroup(
      74,
      value,
    );
  },
  // 91 — persistent vertex identifier, same semantics as AcDbPolyline's 91.
  91: (draft, value) => {
    draft.numbers.vertexId = parseNumberGroup(91, value);
  },
};
